/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from '@tanstack/react-query';
import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getInstanceStatus,
  isInstanceStatusEnabled,
  login,
} from '../../api/login';
import { Constants } from '../../constants/login';
import { getCompanySubdomain } from '../../helpers';
import {
  getLocalStorageToken,
  getMfaPendingSession,
} from '../../helpers/localStorage';
import { openSessionFromLogin } from '../../helpers/session';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { unwrap } from '../../lib/envelope';

interface UseLoginHook {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  showPassword: boolean;
  toggleShowPassword: () => void;
  submitLoading: boolean;
  statusLoading: boolean;
  loginUnavailable: boolean;
  /** Which API authenticates: the avaliahub-api when its URL is set. */
  authSourceNote: string;
  handleSubmit: (e: FormEvent) => Promise<void>;
  goToForgotPassword: () => void;
}

export default function useLogin(): UseLoginHook {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const companySubdomain = getCompanySubdomain();

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const navigate = useNavigate();
  const token = getLocalStorageToken();

  // A failed check shows the form anyway: /login/control itself answers 503
  // when the instance cannot issue sessions, and that lands on the snackbar.
  const { data: instanceStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['instanceStatus'],
    queryFn: ({ signal }) => getInstanceStatus(signal).then(unwrap),
    enabled: isInstanceStatusEnabled,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    // A login left half-done resumes on the MFA screen, not on /home: the
    // pending token is refused everywhere else (REAL-3266).
    if (getMfaPendingSession()) {
      navigate('/mfa');
      return;
    }
    if (token !== null && token !== 'undefined') {
      navigate('/home');
    }
  }, [navigate, token]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimEmail = email.trim();

    try {
      setSubmitLoading(true);
      const response = await login({
        email: trimEmail,
        password,
        companySubdomain,
      });
      navigate(openSessionFromLogin(response));
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleShowPassword = (): void => {
    setShowPassword((value) => !value);
  };

  const goToForgotPassword = (): void => {
    navigate('/forgot-password');
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword,
    submitLoading,
    statusLoading,
    loginUnavailable: instanceStatus?.data?.local_login_enabled === false,
    authSourceNote: isInstanceStatusEnabled
      ? Constants.authLocal
      : Constants.authControlCenter,
    handleSubmit,
    goToForgotPassword,
  };
}
